import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { formatWizardErrorForDisplay } from '../utils/wizardErrorHandling';

/**
 * Enhanced error display component for wizard operations
 */
const WizardErrorDisplay = ({
  error,
  onRetry,
  onDismiss,
  context = '',
  showDetails = false,
  className = ''
}) => {
  const { theme } = useTheme();

  if (!error) return null;

  // Format error for display
  const formattedError = formatWizardErrorForDisplay(error, context);

  // Get error styling based on severity
  const getErrorStyling = () => {
    const baseClasses = 'rounded-lg border-l-4 p-4';
    
    switch (formattedError.severity) {
      case 'low':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-yellow-900/20 border-yellow-500 text-yellow-300'
            : 'bg-yellow-50 border-yellow-400 text-yellow-800'
        }`;
      case 'medium':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-orange-900/20 border-orange-500 text-orange-300'
            : 'bg-orange-50 border-orange-400 text-orange-800'
        }`;
      case 'high':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-red-900/20 border-red-500 text-red-300'
            : 'bg-red-50 border-red-400 text-red-800'
        }`;
      case 'critical':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-red-900/30 border-red-600 text-red-200'
            : 'bg-red-100 border-red-500 text-red-900'
        }`;
      default:
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-600 text-gray-300'
            : 'bg-gray-50 border-gray-400 text-gray-800'
        }`;
    }
  };

  // Get error icon based on severity
  const getErrorIcon = () => {
    const iconClasses = 'w-5 h-5 flex-shrink-0';
    
    switch (formattedError.severity) {
      case 'low':
        return (
          <svg className={iconClasses} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'medium':
      case 'high':
      case 'critical':
        return (
          <svg className={iconClasses} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className={iconClasses} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zM9 14a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div className={`${getErrorStyling()} ${className}`} role="alert">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getErrorIcon()}
        </div>
        
        <div className="ml-3 flex-1">
          {/* Error Title */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">
              {formattedError.severity === 'critical' ? 'Critical Error' :
               formattedError.severity === 'high' ? 'Error' :
               formattedError.severity === 'medium' ? 'Warning' :
               'Notice'}
              {formattedError.step && ` (${formattedError.step})`}
            </h3>
            
            {/* Dismiss Button */}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className={`ml-2 flex-shrink-0 rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 focus:ring-gray-500'
                    : 'hover:bg-gray-100 focus:ring-gray-500'
                }`}
                aria-label="Dismiss error"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Error Message */}
          <div className="mt-2 text-sm">
            <p>{formattedError.userMessage}</p>
            
            {/* Context Information */}
            {context && (
              <p className="mt-1 opacity-75">
                Context: {context}
              </p>
            )}
            
            {/* Field Information */}
            {formattedError.field && (
              <p className="mt-1 opacity-75">
                Field: {formattedError.field}
              </p>
            )}
          </div>
          
          {/* Detailed Error Information */}
          {(showDetails || formattedError.showDetails) && (
            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-medium opacity-75 hover:opacity-100">
                Technical Details
              </summary>
              <div className="mt-2 text-xs opacity-75 font-mono bg-black bg-opacity-20 p-2 rounded">
                <p><strong>Code:</strong> {formattedError.code}</p>
                <p><strong>Message:</strong> {formattedError.message}</p>
                <p><strong>Timestamp:</strong> {new Date(formattedError.timestamp).toLocaleString()}</p>
                {formattedError.step && <p><strong>Step:</strong> {formattedError.step}</p>}
                {formattedError.field && <p><strong>Field:</strong> {formattedError.field}</p>}
              </div>
            </details>
          )}
          
          {/* Action Buttons */}
          <div className="mt-4 flex space-x-3">
            {/* Retry Button */}
            {formattedError.canRetry && onRetry && (
              <button
                onClick={onRetry}
                className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                  theme === 'dark'
                    ? 'text-white bg-teal-600 hover:bg-teal-700 focus:ring-teal-500'
                    : 'text-white bg-teal-600 hover:bg-teal-700 focus:ring-teal-500'
                }`}
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry
              </button>
            )}
            
            {/* Help/Support Link */}
            {formattedError.severity === 'critical' && (
              <a
                href="/support"
                className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                  theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
                }`}
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Get Help
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardErrorDisplay;
